import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ProfileAwarenessAlert, { ProfileAwarenessBadge } from '../common/ProfileAwarenessAlert';

function MealCard({ meal, onRemove, onSwap, compact = false, isFiltered = false, expandable = false }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasCautions = meal?.isFiltered === true || meal?.considerations?.length > 0 || meal?.filterReasons?.length > 0;
  const effectiveFiltered = isFiltered || hasCautions;
  const hasAllergens = meal?.hasAllergens === true && meal?.allergens?.length > 0;
  /* Show considerations; if backend sent filterReasons only, use those as "things to note" */
  const displayConsiderations = meal?.considerations?.length > 0 ? meal.considerations : (meal?.filterReasons || []);

  if (!meal) return null;
  
  const getKeyNutrients = () => {
    const nutrients = [];
    
    // First try to use extracted nutrient values
    if (meal.iron) nutrients.push({ name: 'Iron', value: meal.iron, unit: 'mg' });
    if (meal.protein) nutrients.push({ name: 'Protein', value: meal.protein, unit: 'g' });
    if (meal.folate) nutrients.push({ name: 'Folate', value: meal.folate, unit: 'mcg' });
    if (meal.calcium) nutrients.push({ name: 'Calcium', value: meal.calcium, unit: 'mg' });
    if (meal.fiber) nutrients.push({ name: 'Fiber', value: meal.fiber, unit: 'g' });
    if (meal.omega3) nutrients.push({ name: 'Omega-3', value: meal.omega3, unit: 'g' });
    
    // If no extracted values, use nutrients array from JSON
    if (nutrients.length === 0 && meal.nutrients && Array.isArray(meal.nutrients)) {
      const nutrientNames = meal.nutrients.slice(0, 3);
      return nutrientNames.map(name => ({ name, value: null, unit: '' }));
    }
    
    return nutrients.slice(0, 3);
  };
  
  const getDifficultyColor = () => {
    switch (meal.difficulty) {
      case 'EASY': return '#4CAF50';
      case 'MEDIUM': return '#FFC107';
      case 'HARD': return '#F44336';
      default: return '#9E9E9E';
    }
  };
  
  return (
    <motion.div
      className={`meal-card ${compact ? 'compact' : ''} ${effectiveFiltered ? 'filtered' : ''} ${hasAllergens ? 'has-allergens' : ''} ${expandable ? 'expandable' : ''} ${expandable && isExpanded ? 'expanded' : ''}`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: effectiveFiltered ? 0.92 : 1, scale: 1 }}
      whileHover={expandable && !isExpanded ? { scale: 1.02, y: -2 } : {}}
      transition={{ duration: 0.2 }}
    >
      {meal.imageUrl && (
        <div className="meal-image" style={{ backgroundImage: `url(${meal.imageUrl})` }}>
          {!meal.imageUrl && <div className="meal-image-placeholder">🍽️</div>}
        </div>
      )}
      
      <div className="meal-content">
        <div className="meal-header">
          <div className="meal-header-left">
            <h4 className="meal-name">{meal.name}</h4>
            <ProfileAwarenessBadge meal={meal} type="meal" />
          </div>
          {(meal.prepTime || meal.prepTimeMinutes) && (
            <span className="meal-prep-time">
              ⏱️ {meal.prepTimeMinutes ? `${meal.prepTimeMinutes} min` : meal.prepTime}
            </span>
          )}
        </div>
        
        {/* Profile Awareness Alerts */}
        <ProfileAwarenessAlert 
          meal={meal} 
          type="meal" 
          compact={compact || expandable}
        />

        {/* Caution: allergen or server-side caution (user can still pick with caution) */}
        {(meal.cautionText || (meal.hasAllergens && meal.allergens?.length)) && !compact && (
          <div className="meal-caution-banner" role="alert">
            <span className="meal-caution-icon">⚠️</span>
            <span className="meal-caution-text">
              {meal.cautionText || `Contains your allergen(s): ${(meal.allergens || []).join(', ')}. You can still add this with caution.`}
            </span>
          </div>
        )}
        
        {/* Why we recommend this / Match reasons - full details when expanded */}
        {(!expandable || isExpanded) && (meal.whyRecommended || (meal.matchReasons && meal.matchReasons.length > 0)) && !compact && (
          <div className="meal-why-recommended">
            <strong className="meal-why-label">Why we recommend this</strong>
            {meal.whyRecommended && (
              <p className="meal-why-text">{meal.whyRecommended}</p>
            )}
            {meal.matchReasons && meal.matchReasons.length > 0 && (
              <ul className="meal-match-reasons">
                {meal.matchReasons.map((reason, idx) => (
                  <li key={idx}>{reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Considerations / filter reasons (why it might not be ideal – we still show it) */}
        {(!expandable || isExpanded) && displayConsiderations.length > 0 && !compact && (
          <div className="meal-considerations">
            <strong className="meal-considerations-label">
              {meal.isFiltered ? 'Why we still show it' : 'Things to note'}
            </strong>
            <ul className="meal-considerations-list">
              {displayConsiderations.map((c, idx) => (
                <li key={idx}>{c}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Compact Summary View - Shown when expandable and collapsed */}
        {expandable && !isExpanded && (
          <div className="meal-summary-compact">
            {/* One-line teaser: why we recommend (or first consideration) */}
            {(meal.whyRecommended || (meal.matchReasons && meal.matchReasons.length > 0) || displayConsiderations.length > 0) && (
              <p className="meal-summary-teaser">
                {meal.whyRecommended
                  ? (meal.whyRecommended.length > 80 ? meal.whyRecommended.substring(0, 80) + '…' : meal.whyRecommended)
                  : meal.matchReasons?.length > 0
                    ? `✓ ${meal.matchReasons[0]}`
                    : displayConsiderations.length > 0
                      ? `📌 ${displayConsiderations[0]}`
                      : null}
              </p>
            )}
            {meal.description && (
              <p className="meal-description-preview">
                {meal.description.length > 100 ? `${meal.description.substring(0, 100)}...` : meal.description}
              </p>
            )}
            
            <div className="meal-nutrients-compact">
              {getKeyNutrients().slice(0, 3).map((nutrient, idx) => (
                <span key={idx} className="nutrient-badge-compact">
                  {nutrient.name}{nutrient.value ? `: ${nutrient.value}${nutrient.unit}` : ''}
                </span>
              ))}
            </div>
            
            <button
              type="button"
              className="expand-details-button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsExpanded(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <span>View Full Details</span>
              <span>▼</span>
            </button>
          </div>
        )}
        
        {/* Full Details View - Shown when expanded or not expandable */}
        {(!expandable || isExpanded) && (
          <motion.div
            key={`meal-details-${isExpanded}`}
            className="meal-details-full"
            initial={expandable ? { opacity: 0, y: -10 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
              {meal.description && (
                <p className="meal-description">{meal.description}</p>
              )}
              
              {/* Personalization Tags - Show at top if available */}
              {meal.personalizationTags && meal.personalizationTags.length > 0 && !compact && (
          <div className="meal-personalization-tags">
            {meal.personalizationTags.slice(0, 3).map((tag, idx) => {
              const getTagIcon = (tag) => {
                const icons = {
                  'Quick Prep': '⚡', 'Moderate Prep': '⏱️', 'Your Favorite': '❤️',
                  'Skill Matched': '👨‍🍳', 'Nausea-Friendly': '🤢', 'Anti-Nausea': '🌿',
                  'Spicy': '🌶️', 'Mild': '😊', 'Sweet': '🍯', 'Energy Boost': '⚡', 'Low Effort': '💪'
                };
                return icons[tag] || '✨';
              };
              const getTagColor = (tag) => {
                const colors = {
                  'Quick Prep': '#10B981', 'Moderate Prep': '#3B82F6', 'Your Favorite': '#EC4899',
                  'Skill Matched': '#8B5CF6', 'Nausea-Friendly': '#F59E0B', 'Anti-Nausea': '#10B981',
                  'Spicy': '#EF4444', 'Mild': '#3B82F6', 'Sweet': '#F59E0B', 'Energy Boost': '#10B981', 'Low Effort': '#6366F1'
                };
                return colors[tag] || '#6B7280';
              };
              return (
                <motion.span
                  key={idx}
                  className="meal-personalization-tag"
                  style={{
                    backgroundColor: `${getTagColor(tag)}15`,
                    borderColor: getTagColor(tag),
                    color: getTagColor(tag)
                  }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <span className="tag-icon">{getTagIcon(tag)}</span>
                  <span className="tag-text">{tag}</span>
                </motion.span>
              );
            })}
              </div>
              )}
              
              {meal.recipe && !compact && (
          <div className="meal-recipe">
            <div className="recipe-header">
              <span className="recipe-icon">📝</span>
              <strong className="recipe-title">How to Prepare:</strong>
            </div>
            <div className="recipe-steps">
              {(() => {
                // Try to split by numbered steps (1. 2. 3. etc.)
                const numberedSteps = meal.recipe.match(/\d+\.\s*[^\d]+/g);
                if (numberedSteps && numberedSteps.length > 1) {
                  // Recipe has numbered steps
                  return numberedSteps.map((step, idx) => {
                    const stepText = step.replace(/^\d+\.\s*/, '').trim();
                    return (
                      <div key={idx} className="recipe-step">
                        <span className="step-number">{idx + 1}.</span>
                        <span className="step-text">{stepText}</span>
                      </div>
                    );
                  });
                } else {
                  // Recipe is plain text or single paragraph - split by periods or newlines
                  const steps = meal.recipe
                    .split(/(?:\d+\.\s*|\.\s+(?=[A-Z])|\n)/)
                    .filter(step => step.trim() && step.trim().length > 10);
                  
                  if (steps.length > 1) {
                    return steps.map((step, idx) => (
                      <div key={idx} className="recipe-step">
                        <span className="step-number">{idx + 1}.</span>
                        <span className="step-text">{step.trim()}</span>
                      </div>
                    ));
                  } else {
                    // Single paragraph - display as is
                    return (
                      <div className="recipe-step">
                        <span className="step-text" style={{ paddingLeft: 0 }}>{meal.recipe}</span>
                      </div>
                    );
                  }
                }
              })()}
            </div>
              </div>
              )}
              
              {meal.ingredients && !compact && (
          <div className="meal-ingredients">
            <div className="ingredients-header">
              <span className="ingredients-icon">🥘</span>
              <strong className="ingredients-title">Ingredients:</strong>
            </div>
            <div className="ingredients-list">
              {meal.ingredients.map((ingredient, idx) => (
                <span key={idx} className="ingredient-item">
                  {ingredient}
                </span>
              ))}
            </div>
              </div>
              )}
              
              <div className="meal-nutrients">
                {getKeyNutrients().map((nutrient, idx) => (
                  <span key={idx} className="nutrient-badge">
                    {nutrient.name}{nutrient.value ? `: ${nutrient.value}${nutrient.unit}` : ''}
                  </span>
                ))}
              </div>
              
              {meal.source && !compact && (
                <div className="meal-source">
                  <span className="source-label">📚 Source:</span>
                  <span className="source-text">{meal.source}</span>
                </div>
              )}
              
              {expandable && isExpanded && (
                <motion.button
                  type="button"
                  className="collapse-details-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsExpanded(false);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Show Less</span>
                  <motion.span
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    ▲
                  </motion.span>
                </motion.button>
              )}
          </motion.div>
        )}
        
        {meal.source && compact && (
          <div className="meal-source compact">
            <span className="source-text">{meal.source}</span>
          </div>
        )}
        
        <div className="meal-footer">
          {meal.difficulty && (
            <span
              className="difficulty-badge"
              style={{ backgroundColor: getDifficultyColor() + '20', color: getDifficultyColor() }}
            >
              {meal.difficulty}
            </span>
          )}
          
          {meal.tags && meal.tags.length > 0 && (
            <div className="meal-tags">
              {meal.tags.slice(0, 2).map((tag, idx) => (
                <span key={idx} className="meal-tag">{tag.replace('_', ' ')}</span>
              ))}
            </div>
          )}
        </div>
        
        {!compact && (
          <div className="meal-actions">
            {onSwap && (
              <motion.button
                className="action-button swap-button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onSwap();
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                🔄 Swap
              </motion.button>
            )}
            {onRemove && (
              <motion.button
                className="action-button remove-button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onRemove();
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                ✕ Remove
              </motion.button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default MealCard;

